<template>
  <ASiteContainer v-if="trip">
    <h1 class="text-3xl font-black md:text-4xl">{{ trip.name }}</h1>
    <h2 class="mt-2 text-xl font-medium md:text-2xl">
      {{
        trip.subtitle
          || $t("pages.trips.subtitle", {
            days: trip.duration,
            destination: trip.destinations[0]?.name!,
          })
      }}
    </h2>

    <div
      v-if="trip.categories && trip.categories.length"
      class="mt-2 flex flex-wrap gap-2"
    >
      <UButton
        v-for="category in trip.categories"
        :key="category.label"
        size="2xs"
        :to="
          localePath({
            name: 'trips-category',
            params: { category: category.slug },
          })
        "
        variant="soft"
        color="gray"
        class="py-0.5"
      >
        {{ category.label }}
      </UButton>
    </div>

    <div
      class="relative mb-8 mt-6 flex flex-col gap-2 overflow-hidden rounded-2xl md:flex-row"
    >
      <template v-if="trip.images?.length >= 5">
        <div class="w-full md:w-1/2">
          <AImg
            class="h-64 w-full cursor-pointer object-cover duration-300 hover:opacity-90 md:h-full"
            height="520"
            width="640"
            :src="trip.images[0]?.url"
            alt="Célébration de Holi"
            @click="showImages(0)"
          />
        </div>
        <div class="grid w-full grid-cols-2 gap-2 md:w-1/2">
          <AImg
            v-for="(image, index) in trip.images?.slice(1, 5)"
            :key="index"
            class="h-32 w-full cursor-pointer object-cover duration-300 hover:opacity-90 md:h-64"
            height="400"
            width="320"
            :src="image.url"
            :alt="`Image Holi ${index + 1}`"
            @click="showImages(index + 1)"
          />
        </div>
        <UButton
          v-if="trip.images?.length && trip.images?.length > 5"
          variant="solid"
          color="white"
          class="absolute bottom-4 right-4 border border-white bg-white/75 text-sm ring-1 ring-gray-600 transition-all duration-300 hover:bg-white md:text-base"
          @click="showImages(0)"
        >
          <UIcon name="mdi:image-outline" class="size-3 md:size-4" />
          {{ $t("pages.trips.seeAllPictures") }}
        </UButton>
      </template>
      <AImg
        v-else
        :src="trip.images[0]?.url"
        height="640"
        width="1280"
        class="w-full cursor-pointer object-cover duration-300 hover:opacity-90"
      />
    </div>

    <p class="whitespace-pre-wrap text-base md:text-lg">
      {{ trip.introduction }}
    </p>

    <div class="mt-8 flex flex-col gap-8 md:flex-row">
      <div class="w-full md:w-2/3 md:pr-24">
        <div>
          <h3 class="mb-6 flex items-center text-xl font-bold md:text-2xl">
            <UIcon
              name="fluent:slide-text-sparkle-28-filled"
              class="mr-3 size-6 md:size-7"
            />
            {{ $t("pages.trips.descriptionOfTheTrip") }}
          </h3>
          <div class="whitespace-pre-wrap md:text-lg">
            {{ trip.description }}
          </div>
        </div>

        <hr class="my-8 md:my-12" />

        <div>
          <h3 class="mb-6 flex items-center text-xl font-bold md:text-2xl">
            <UIcon name="fluent:bed-24-filled" class="mr-3 size-6 md:size-7" />
            {{ $t("pages.trips.accomodation") }}
          </h3>
          <div class="whitespace-pre-wrap">
            {{ trip.accomodation || $t("pages.trips.accomodationPlaceholder") }}
          </div>
        </div>

        <hr class="my-8 md:my-12" />

        <div v-if="trip.trip_days.length">
          <h3 class="mb-6 flex items-center text-xl font-bold md:text-2xl">
            <UIcon name="mdi:calendar-clock" class="mr-3 size-6 md:size-7" />
            {{ $t("pages.trips.programDayByDay") }}
          </h3>
          <ul class="space-y-4 md:space-y-6">
            <li
              v-for="(day, index) in trip.trip_days"
              :key="index"
              class="rounded-lg bg-gray-50 p-4 md:p-6"
            >
              <h4 class="mb-2 text-lg font-semibold md:text-xl">
                <div
                  class="flex flex-col items-start text-[1.2rem] font-bold sm:items-center md:flex-row"
                >
                  <p
                    v-if="day.day"
                    class="mr-2 text-nowrap lowercase text-gray-500 first-letter:uppercase"
                  >
                    {{ $t("pages.trips.day", { day: day.day }) }}
                  </p>
                  {{ day.title }}
                </div>
              </h4>
              <div class="group/carousel mb-5 mt-3">
                <UCarousel
                  v-if="day.images && day.images.length"
                  v-slot="{ item }"
                  :arrows="day.images.length > 1"
                  indicators
                  :ui="{ item: 'basis-full' }"
                  :items="day.images"
                >
                  <AImg
                    :src="item.url"
                    :alt="item.alt || day.title"
                    width="1400"
                    height="1000"
                    class="w-full rounded-xl object-cover sm:max-h-[500px]"
                  />
                </UCarousel>
              </div>
              <p
                style="overflow-wrap: anywhere"
                class="whitespace-pre-wrap text-base md:text-lg"
              >
                {{ day.description }}
              </p>
            </li>
          </ul>
        </div>
      </div>

      <div
        class="mt-8 w-full overflow-hidden sm:-mx-3 sm:overflow-visible sm:px-3 md:mt-0 md:w-1/3"
      >
        <div
          class="fixed inset-x-0 bottom-0 z-10 w-full max-w-full border-t border-none border-gray-200 bg-white px-6 pb-6 pt-4 shadow-[0_-5px_10px_10px_rgba(0,0,0,0.1)] sm:inset-x-auto sm:w-auto sm:rounded-2xl sm:!border sm:border-solid sm:shadow-lg md:sticky md:top-28 md:p-8"
        >
          <ul
            class="mb-2 flex max-w-full space-x-4 overflow-x-auto whitespace-nowrap pb-2 sm:mb-6 sm:pb-0 md:block md:space-x-0 md:space-y-3 md:whitespace-normal"
          >
            <li v-if="trip.duration" class="flex shrink-0 items-center">
              <strong class="pr-2"
              >{{ $t("pages.trips.detailsOfTheTrip.duration") }} :</strong
              >
              {{ $t("pages.trips.duration", { duration: trip.duration }) }}
            </li>

            <li v-if="trip.people_max" class="flex shrink-0 items-center">
              <strong class="pr-2"
              >{{ $t("pages.trips.detailsOfTheTrip.groupSize") }} :</strong
              >
              {{ trip.people_max }}
              {{ $t("pages.trips.detailsOfTheTrip.groupSize.max") }}
            </li>
            <li v-if="trip.priceHt" class="flex shrink-0 items-center">
              <strong class="pr-2"
              >{{ $t("pages.trips.detailsOfTheTrip.price") }} :</strong
              >
              {{
                $t("pages.trips.detailsOfTheTrip.price.from", {
                  price: currency(trip.priceHt),
                })
              }}
            </li>
            <li v-if="trip.destinations?.length" class="shrink-0">
              <b>{{ $t("pages.trips.detailsOfTheTrip.destinations") }}</b>
              {{
                trip.destinations
                  .map((destination: any) => destination.name)
                  .join(", ")
              }}
            </li>
          </ul>
          <UButton
            :disabled="!trip.publishedAt"
            color="brand"
            class="relative z-20 w-2/3 justify-center shadow sm:w-full sm:text-[1rem] sm:font-bold"
            size="lg"
            @click="newQuoteWithTrip(trip.id)"
          >
            {{ $t("pages.trips.detailsOfTheTrip.bookThisTrip") }}
          </UButton>
          <ABorderBeam
            v-if="!$device.isMobile"
            color-from="#222"
            :width="300"
            color-to="transparent"
            :height="300"
            class="!-inset-px"
            :duration="20"
          />
        </div>
      </div>
    </div>
  </ASiteContainer>
</template>

<script lang="ts" setup>
import { api as viewerApi } from 'v-viewer'

const route = useRoute()
const localePath = useLocalePath()
const domain = useRequestURL().origin

definePageMeta({
  breadcrumb: {
    name: undefined,
  },
})

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    de: '/reisen/[slug]',
    en: '/trips/[slug]',
    es: '/viajes/[slug]',
    fr: '/sejours/[slug]',
    it: '/viaggi/[slug]',
    nl: '/reizen/[slug]',
    pt: '/viagens/[slug]',
  },
})

const { data: trip, error: tripError } = await useFetch<
  Bff['/api/trips/:slug']
>(`/api/trips/${route.params.slug}`, {
  query: {
    token: route.query.token,
  },
})
assertDefined(trip, tripError)
route.meta.breadcrumb!.name = trip.value.name

useSchemaOrg([
  {
    '@type': 'TouristTrip',
    'name': trip.value.name,
    'description': trip.value.introduction,
    'touristType': [],
    'includesAttraction':
        trip.value.trip_days?.map(day => ({
          '@type': 'TouristAttraction',
          'name': day.title,
          'description': day.description,
        })) || [],
  },
])

const { newQuoteWithTrip } = useQuoteStore()

function showImages(index: number = 0) {
  if (!trip.value || trip.value.images.length === 0)
    return

  viewerApi({
    images: trip.value.images.map((image: any) => image.url),
    options: {
      initialViewIndex: index,
    },
  })
}

useSeoMeta({
  description:
    trip.value.metaDescription
    || trip.value.subtitle
    || trip.value.introduction
    || truncate(trip.value.description, 140)
    || trip.value.name,
  ogDescription: trip.value.metaDescription || trip.value.subtitle,
  ogImage: trip.value.images[0]?.url,
  ogTitle: trip.value.metaTitle || trip.value.name,
  title: trip.value.metaTitle || trip.value.name,
})

useHead({
  link: [
    {
      href: `${domain}${localePath({ name: 'trips-slug', params: { slug: trip.value.slug } })}`,
      rel: 'canonical',
    },
  ],
})
</script>
